import { diffLines, diffWords } from 'diff';

export type TextDiffLineType = 'removed' | 'added' | 'unchanged';

export type TextDiffLine = {
  type: TextDiffLineType;
  content: string;
};

const LINE_DIFF_FIELDS = new Set([
  'description',
  'shortDescription',
  'poc',
  'businessImpact',
  'mitigationPlan',
  'findingUpdateComment',
]);

export function formatDiffValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value, null, 2);
}

export function resolveDiffMode(field?: string): 'words' | 'lines' {
  if (field && LINE_DIFF_FIELDS.has(field)) {
    return 'lines';
  }
  return 'words';
}

export function buildTextDiffLines({
  before,
  after,
  field,
}: {
  before: unknown;
  after: unknown;
  field?: string;
}): TextDiffLine[] {
  const beforeText = formatDiffValue(before);
  const afterText = formatDiffValue(after);
  const mode = resolveDiffMode(field);
  const parts =
    mode === 'lines'
      ? diffLines(beforeText, afterText)
      : diffWords(beforeText, afterText);

  const hasChange = parts.some((part) => part.added || part.removed);
  if (!hasChange) {
    return [];
  }

  return parts.flatMap((part) => {
    const type: TextDiffLineType = part.added
      ? 'added'
      : part.removed
        ? 'removed'
        : 'unchanged';

    if (mode === 'lines') {
      const lines = part.value.split('\n');
      const trimmedLines =
        lines.length > 0 && lines[lines.length - 1] === ''
          ? lines.slice(0, -1)
          : lines;
      return trimmedLines.map((line) => ({
        type,
        content: line,
      }));
    }

    return [{ type, content: part.value }];
  });
}
